import React, { FC } from 'react';
import { Controller } from 'react-hook-form';

import { useI18n } from 'i18n';
import { useAttributeDetectorGeneralFields } from 'hooks/attribute-range-detectors/useAttributeDetectorGeneralFields';
import Input from 'core/react/general/form/Input';
import Button from 'core/react/general/form/Button';
import Checkbox from 'core/react/general/form/Checkbox';
import InputPicker from 'core/react/general/form/InputPicker';
import InputNumber from 'core/react/general/form/InputNumber';

import { FieldErrorStyled, GeneralFieldsStyled } from 'ui/pages/attribute-range-detectors/detector-form.styled';

interface IGeneralFieldsFieldsProps {
  isDisabled: boolean;
  error: string | null;
}

export const GeneralFields: FC<IGeneralFieldsFieldsProps> = ({ isDisabled, error }) => {
  const { f, fc } = useI18n();

  const {
    control,
    errors,
    eventTypesOptions,
    isOnInRange,
    isOnOutRange,
    isStartTimeout,
    isEndTimeout,
    handleRangeChange,
    handleTimeoutChange,
  } = useAttributeDetectorGeneralFields();

  const validateTimeout = (value: string) => (Number(value) < 0 ? fc('value must be positive') : true);

  //TODO: we should get error form fieldState when react-hook-form is upgraded
  return (
    <GeneralFieldsStyled>
      <Controller
        control={control}
        name="name"
        rules={{ required: fc('please enter a value') }}
        render={({ value, onChange }) => (
          <div>
            <Input autoFocus label={f('name')} disabled={isDisabled} value={value} onChange={onChange} />
            {/*TODO: error messages should be moved to corresponding form components when they are being refactored*/}
            {!!errors.name?.message && <FieldErrorStyled>{errors.name.message}</FieldErrorStyled>}
          </div>
        )}
      />
      <Controller
        control={control}
        name="onIn"
        render={({ value }) => (
          <Checkbox label={fc('in range')} disabled={isDisabled} value={value} onChange={handleRangeChange('onIn')} />
        )}
      />
      <Controller
        control={control}
        name="inEventType"
        render={({ value, onChange }) => (
          <InputPicker
            disabled={!isOnInRange}
            cleanable={false}
            data={eventTypesOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="onOut"
        render={({ value }) => (
          <Checkbox
            label={fc('out of range')}
            disabled={isDisabled}
            value={value}
            onChange={handleRangeChange('onOut')}
          />
        )}
      />
      <Controller
        control={control}
        name="outEventType"
        render={({ value, onChange }) => (
          <InputPicker
            disabled={!isOnOutRange}
            cleanable={false}
            data={eventTypesOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="isStartTimeout"
        render={({ value }) => (
          <Checkbox
            label={fc('start timeout')}
            disabled={isDisabled}
            value={value}
            onChange={handleTimeoutChange('isStartTimeout')}
          />
        )}
      />
      <Controller
        control={control}
        name="startTimeout"
        rules={{ validate: validateTimeout }}
        render={({ value, onChange }) => (
          <InputNumber disabled={!isStartTimeout || isDisabled} value={value} onChange={onChange} />
        )}
      />
      {!!errors.startTimeout?.message && <FieldErrorStyled>{errors.startTimeout.message}</FieldErrorStyled>}
      <Controller
        control={control}
        name="isEndTimeout"
        render={({ value }) => (
          <Checkbox
            label={fc('end timeout')}
            disabled={isDisabled}
            value={value}
            onChange={handleTimeoutChange('isEndTimeout')}
          />
        )}
      />
      <Controller
        control={control}
        name="endTimeout"
        rules={{ validate: validateTimeout }}
        render={({ value, onChange }) => (
          <InputNumber disabled={!isEndTimeout || isDisabled} value={value} onChange={onChange} />
        )}
      />
      {!!errors.endTimeout?.message && <FieldErrorStyled>{errors.endTimeout.message}</FieldErrorStyled>}
      <Button type="submit" disabled={isDisabled}>
        {fc('save')}
      </Button>
    </GeneralFieldsStyled>
  );
};
